/**
 * The terms a rate guide states in prose, stated once for both ends that read them.
 *
 * ## Why this is a schema rather than columns
 *
 * `tariff_versions.document` is JSONB because the maintainer's real price list has not
 * arrived, and a column per term is a migration per term it turns out to have — on a
 * populated table, leaving a NULL every earlier edition must be read around.
 * `AcceptedPriceSnapshot`'s own docblock makes that argument about the snapshot; migration
 * 0026's header makes it about the thing the snapshot is computed *from*. JSONB with no
 * shape, though, is how a schema stops being one. This is the shape.
 *
 * ## Why it lives here and not in `packages/db`
 *
 * The document is parsed **twice**: by `pnpm load:reference` when an edition is published,
 * and by the pricing engine on every read. The second is the half that matters — a row
 * written by the owner role at a psql prompt must refuse to price rather than price
 * strangely. Two parsers are free to disagree, and a disagreement here is a mispriced
 * Order, so the rule is stated once in the pure package both sides already reach.
 *
 * That made `packages/db` depend on `@tard/contracts` for the first time, and at the time it
 * was the deliberate inversion of the reasoning that kept `KNOWN_POLICY_KEYS` local to the
 * loader (ADR-0059 §3): a policy key was a **name**, and one hand-kept list the honest cost of
 * that. This is **arithmetic**, and there is no honest cost of writing it twice. In M10 that
 * list followed this schema here, once every key acquired a value shape of its own
 * (`policy.ts`, ADR-0061) — the argument this paragraph makes is the one that moved it.
 *
 * ## Every object is `.strict()`, against this package's own convention
 *
 * Every other schema here is default-strip, which is right at an HTTP ingress: an unknown
 * key is the client's business and dropping it is the polite thing. A tariff document is
 * neither a request nor transient — it is persisted and priced from. Under strip, a rate
 * guide carrying a term this platform does not implement would load cleanly, charge
 * nothing for it, and go on doing so for as long as the edition is in force. Refusing at
 * the door is what makes "the schema expresses a superset of a document nobody has read"
 * a check rather than a hope.
 *
 * **Nothing here is type-checked by `tsc` in the test file**, because every package
 * excludes `*.test.ts` from its build. So a `.strict()` missing from an inner object is
 * invisible to everything except a case that tries an unknown key at that exact path, and
 * `tariff.test.ts` walks all of them.
 *
 * ## Money, and the empty string
 *
 * Every money value is a **string** of minor units and every quantity that divides is a
 * string of digits. `BigInt("")` is `0n` rather than a throw, so an empty amount would
 * price a surcharge at zero silently; the regexes below refuse it at the type, once,
 * rather than at each of the dozen `BigInt(...)` calls the engine will make.
 *
 * `bps`, `shareBps` and `sequence` are the only JSON numbers besides `docSchema` — none is
 * money, and all are small integers a double represents exactly.
 *
 * ## What this schema does not check
 *
 * A surcharge `code` is checked for **shape** here and for **membership** by the database:
 * `tariff_surcharge_zones.surcharge_code` is a foreign key to `fee_codes`, and migration
 * 0026's coherence trigger refuses a document naming a code that table does not hold. That
 * is the boundary rule the migration states — selected by a predicate, or referentially
 * checked — applied to the parser: contracts cannot import the kernel, and the kernel's
 * four-code `FEE_CODES` is not the database's eight-code vocabulary anyway.
 */
import { z } from 'zod';
import { parcelInputSchema } from './orders.js';
import { renderedText } from './rendered-text.js';
import { RTO_REASONS } from './shipments.js';
/**
 * Minor units on the wire, the exact regex and message `orders.ts` and `custody.ts` use.
 *
 * Copied rather than hoisted into a shared helper: that refactor touches four call sites
 * in three files and belongs to its own commit, not to the one introducing a fifth.
 */
const minorUnits = z.string().regex(/^\d{1,18}$/u, 'money_minor_units');
/**
 * A quantity that something divides by, so zero is unrepresentable rather than refused
 * later. No leading zeros, matching `load-reference.ts`'s own `INTEGER`.
 */
const positiveInteger = z.string().regex(/^[1-9]\d{0,17}$/u, 'positive_integer_string');
/** A quantity that may legitimately be zero — a floor of none, a threshold of any. */
const nonNegativeInteger = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,17})$/u, 'non_negative_integer_string');
/**
 * `max >= min` where both are set, so the clamp order in the engine is immaterial.
 *
 * Stated here rather than re-derived at every clamp: the review found a clamp written as
 * one line that discarded its own floor whenever no maximum was set, and the fix reads
 * correctly only while this holds.
 */
const clamped = (value) => value.minimumMinor === null ||
    value.maximumMinor === null ||
    BigInt(value.maximumMinor) >= BigInt(value.minimumMinor);
const CLAMP_MESSAGE = { message: 'maximumMinor must not be below minimumMinor', path: ['maximumMinor'] };
// ---------------------------------------------------------------------------
// Closed vocabularies
// ---------------------------------------------------------------------------
// This file is the first artefact in the repository to name these. They are exported so
// the engine, the console and the i18n bundles read one list rather than three.
//
// The first two **are** the schema's source: `z.enum` below derives the union from the
// array, so they cannot drift. The two surcharge vocabularies cannot be built that way —
// a discriminated union needs each variant's own fields — so they carry `satisfies`, which
// catches a member here the schema does not know, and `tariff.test.ts` reads `.options`
// off the unions for the other direction.
export const CHARGEABLE_WEIGHT_BASES = ['per_parcel', 'per_shipment'];
export const MINIMUM_CHARGE_TARGETS = ['line_haul', 'total'];
export const SURCHARGE_KINDS = [
    'percent_of_base',
    'fixed_per_shipment',
    'fixed_per_parcel',
    'per_kg',
    'percent_of_declared_value',
];
export const SURCHARGE_CONDITIONS = [
    'always',
    'destination_zone_listed',
    'chargeable_weight_over',
    'parcel_longest_side_over',
    'parcel_girth_over',
    'declared_value_over',
];
// ---------------------------------------------------------------------------
// A surcharge
// ---------------------------------------------------------------------------
/**
 * How much. Five, because carriers publish five.
 *
 * `percent_of_base` carries its own basis: `line_haul` is the plain form, and
 * `line_haul_plus_prior` is the compounding one some carriers publish for fuel — which is
 * why `sequence` has to be unique, since a percentage of "everything applied so far" is
 * not defined without an order.
 */
export const surchargeKindSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('percent_of_base'),
        bps: z.number().int().min(0),
        basis: z.enum(['line_haul', 'line_haul_plus_prior']),
    })
        .strict(),
    z.object({ type: z.literal('fixed_per_shipment'), amountMinor: minorUnits }).strict(),
    z.object({ type: z.literal('fixed_per_parcel'), amountMinor: minorUnits }).strict(),
    z
        .object({
        type: z.literal('per_kg'),
        amountMinor: minorUnits,
        /** Divides the chargeable weight, so it cannot be zero. */
        perGrams: positiveInteger,
    })
        .strict(),
    /** Insurance. Skipped entirely when no value was declared — never charged as zero. */
    z.object({ type: z.literal('percent_of_declared_value'), bps: z.number().int().min(0) }).strict(),
]);
/**
 * Whether. Six, and the closed set is the point — a condition nobody implemented is a
 * surcharge that never fires, which is the silent species this platform refuses.
 *
 * `destination_zone_listed` names no Zones here: membership is rows in
 * `tariff_surcharge_zones`, so a typo'd or retired Zone is caught by a foreign key rather
 * than by a list inside JSON that nothing can check.
 */
export const surchargeConditionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('always') }).strict(),
    z.object({ type: z.literal('destination_zone_listed') }).strict(),
    z.object({ type: z.literal('chargeable_weight_over'), grams: nonNegativeInteger }).strict(),
    z.object({ type: z.literal('parcel_longest_side_over'), mm: nonNegativeInteger }).strict(),
    /** Longest + 2 × (second + third), the carrier definition. */
    z.object({ type: z.literal('parcel_girth_over'), mm: nonNegativeInteger }).strict(),
    z.object({ type: z.literal('declared_value_over'), amountMinor: minorUnits }).strict(),
]);
/**
 * There is deliberately no label field.
 *
 * A surcharge cannot ship without a `fee_codes` row anyway, so the display name belongs in
 * the three i18n bundles under `fee.code.<code>` — where it is translated, rather than in
 * a database column in one language.
 */
export const tariffSurchargeSchema = z
    .object({
    /**
     * `fee_codes`' own CHECK, shape only. The dot is excluded because
     * `statement.service.ts` reads the code as the third segment of
     * `platform.fee_revenue.{code}.{currency}`, so a code containing one would silently
     * rename a fee on a Merchant's statement.
     */
    code: z.string().regex(/^[a-z][a-z0-9_]*$/u, 'fee_code_shape'),
    /** Evaluation order, unique within the edition. */
    sequence: z.number().int().min(1),
    kind: surchargeKindSchema,
    when: surchargeConditionSchema,
    minimumMinor: minorUnits.nullable(),
    maximumMinor: minorUnits.nullable(),
})
    .strict()
    .refine(clamped, CLAMP_MESSAGE);
// ---------------------------------------------------------------------------
// The return fee as a share (P2-6)
// ---------------------------------------------------------------------------
/**
 * A share of the delivery fee, in basis points: 5000 is half, 10000 the whole of it.
 *
 * **Never more than the whole fee.** A return that costs more than the delivery it unwinds is a
 * different term, and the fixed `rtoFeeMinor` already states any amount at all.
 */
const shareBps = z.number().int().min(0).max(10_000);
/**
 * The return fee stated as a share of the delivery fee, with a share of its own for a reason the
 * rate guide prices otherwise (P2-6, ADR-0059 amended 2026-10-01).
 *
 * The delivery fee is the snapshot's `deliveryFeeMinor`: the carriage charged, after a minimum
 * charge's uplift and before any surcharge, which is what a Merchant's statement calls delivery.
 * Every amount is computed at **acceptance** and pinned on the Order (I2); closing a return only
 * chooses among the pinned amounts by the reason the return was started for.
 *
 * `byReason` keys are `RTO_REASONS`, refused otherwise; a reason it does not name pays the
 * default share. The recommended terms are half the delivery fee, and the whole of it when the
 * Merchant recalls a parcel already picked up: `{ shareBps: 5000, byReason: { merchant_recall:
 * 10000 } }`.
 */
export const tariffRtoFeeSchema = z
    .object({
    shareBps,
    byReason: z.record(z.enum(RTO_REASONS), shareBps).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const tariffDocumentSchema = z
    .object({
    /**
     * Which parser applies. `tariff_versions.doc_schema` must agree — a CHECK says so — so
     * an edition written under an older shape stays readable rather than being migrated,
     * which would edit a price somebody was charged.
     */
    docSchema: z.literal(1),
    chargeableWeight: z
        .object({
        /**
         * The familiar cm³-per-kg divisor — 5000, 6000 — usable unchanged, because
         * millimetres in and grams out cancel the two factors of a thousand exactly.
         *
         * **Null disables volumetric weight**, which is what a migrated flat card is.
         * Zero would be a division by zero wearing a value, and is unrepresentable.
         */
        volumetricDivisor: positiveInteger.nullable(),
        /**
         * `per_parcel` is Σ max(actual, volumetric) over pieces; `per_shipment` is
         * max(Σ actual, Σ volumetric). Per parcel is never cheaper, and is the common
         * published form.
         */
        basis: z.enum(CHARGEABLE_WEIGHT_BASES),
        /** Chargeable weight rounds up to a multiple of this. `"1"` is no rounding. */
        incrementGrams: positiveInteger,
        /** A floor on the whole shipment, below which the tariff bills as if. `"0"` for none. */
        minimumBillableGrams: nonNegativeInteger,
        /**
         * One permitted value, and it is a **disclosure rather than a switch**: it is
         * pinned onto the snapshot so a Merchant can read what was done to their weight.
         * A second value is an ADR, because a tariff that rounded weight down is a
         * different product rather than a configuration.
         */
        roundingRule: z.literal('ceil_to_increment'),
    })
        .strict(),
    minimumCharge: z
        .object({
        amountMinor: minorUnits,
        /** `line_haul` is a minimum transportation charge; `total` is a minimum invoice. */
        appliesTo: z.enum(MINIMUM_CHARGE_TARGETS),
    })
        .strict(),
    codFee: z
        .object({
        /** Basis points on the COD obligation, valued in the tariff's own currency. */
        bps: z.number().int().min(0),
        /** Carriers cap this. Null at both ends is uncapped, which is today's flat card. */
        minimumMinor: minorUnits.nullable(),
        maximumMinor: minorUnits.nullable(),
    })
        .strict()
        .refine(clamped, CLAMP_MESSAGE),
    /**
     * The return fee as a fixed amount, posted at `rto.closed` against `merchant.receivable`,
     * never a carriage line. **Exactly one of this and `rtoFee`** states the return fee.
     *
     * Optional since P2-6, and that is the whole of the change to an edition already sealed:
     * every document published before it carries this and no `rtoFee`, and still parses to the
     * same meaning, which is why `docSchema` did not move.
     */
    rtoFeeMinor: minorUnits.optional(),
    /** The return fee as a share of the delivery fee, by reason (P2-6). */
    rtoFee: tariffRtoFeeSchema.optional(),
    surcharges: z.array(tariffSurchargeSchema),
})
    .strict()
    .refine((document) => new Set(document.surcharges.map((surcharge) => surcharge.sequence)).size ===
    document.surcharges.length, {
    message: 'each surcharge needs its own sequence, or a compounding percentage has no order',
    path: ['surcharges'],
})
    // Neither would leave a return priced at a number nobody chose; both, a fee stated twice that
    // the engine would have to pick between.
    .refine((document) => (document.rtoFeeMinor === undefined) !== (document.rtoFee === undefined), {
    message: 'state the return fee once: rtoFeeMinor or rtoFee',
    path: ['rtoFeeMinor'],
});
// ---------------------------------------------------------------------------
// Publishing and previewing from the console (ADR-0061, M10-3)
// ---------------------------------------------------------------------------
/** `tariff_versions_currency`'s set. */
export const TARIFF_CURRENCIES = ['USD', 'LBP', 'SYP'];
const isoInstant = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/u, 'iso_instant');
/**
 * One cell of the matrix, as `load-reference`'s `TariffCellInput` states it.
 *
 * Both cluster keys are **required and nullable**, never optional: a destination forgotten would
 * be a lane priced for everywhere, and `null` has to be written to mean that.
 */
export const tariffCellInputSchema = z
    .object({
    originClusterId: z.string().min(1).nullable(),
    destinationClusterId: z.string().min(1).nullable(),
    breakFromGrams: nonNegativeInteger,
    breakToGrams: positiveInteger.nullable(),
    baseMinor: minorUnits,
    perKgMinor: minorUnits.nullable(),
    incrementGrams: positiveInteger.nullable(),
})
    .strict();
export const tariffSurchargeZoneInputSchema = z
    .object({ code: z.string().min(1), zoneId: z.string().min(1) })
    .strict();
/**
 * A new edition for one Merchant and service class.
 *
 * **`closePrevious` is stated, never defaulted.** Replacing the edition in force is a decision,
 * and a default would make it one nobody took. **The file-local rules are not restated here** —
 * a duplicate lane, a band ending before it starts, a Zone list for a code the document does not
 * define — because `validateTariffEntry` in `@tard/db` states them for the loader and the route
 * alike, and refuses them with a path and a code.
 */
export const publishTariffSchema = z
    .object({
    serviceClass: z.string().trim().min(1).max(64),
    version: z.string().trim().min(1).max(64),
    currency: z.enum(TARIFF_CURRENCIES),
    effectiveFrom: isoInstant,
    closePrevious: z.boolean(),
    document: tariffDocumentSchema,
    cells: z.array(tariffCellInputSchema).min(1).max(2000),
    surchargeZones: z.array(tariffSurchargeZoneInputSchema).max(2000),
    owner: z.string().trim().min(1).max(200),
    reviewDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format'),
    note: z.string().trim().min(1).max(500),
})
    .strict();
const CLUSTER_ID_EXEMPT = 'must name a City Cluster: tariff_cells cluster ids are foreign keys to city_clusters (id)';
const CELL_NUMBER_EXEMPT = 'digits only, stored in a BIGINT column of tariff_cells and rendered from it, so no leading zero survives';
export const publishTariffRendered = renderedText(publishTariffSchema, {
    prose: ['owner'],
    // The document renders as stored — the edition detail, and its rtoFeeMinor on every accepted Order's
    // price snapshot — and money_minor_units admits a leading zero, so a digit run such as 031234567.
    refs: ['version', 'document'],
    exempt: {
        serviceClass: 'must name a service class: tariff_versions.service_class is a foreign key to service_classes',
        effectiveFrom: 'an ISO instant ending in Z (iso_instant), which holds no phone-shaped digit run',
        'cells.*.originClusterId': CLUSTER_ID_EXEMPT,
        'cells.*.destinationClusterId': CLUSTER_ID_EXEMPT,
        'cells.*.breakFromGrams': CELL_NUMBER_EXEMPT,
        'cells.*.breakToGrams': CELL_NUMBER_EXEMPT,
        'cells.*.baseMinor': CELL_NUMBER_EXEMPT,
        'cells.*.perKgMinor': CELL_NUMBER_EXEMPT,
        'cells.*.incrementGrams': CELL_NUMBER_EXEMPT,
        'surchargeZones.*.code': 'must name a fee code: tariff_surcharge_zones.surcharge_code is a foreign key to fee_codes',
        'surchargeZones.*.zoneId': 'must name a Zone: tariff_surcharge_zones.zone_id is a foreign key to zones (id)',
        reviewDate: 'a YYYY-MM-DD date (review_date_format), which holds no phone-shaped digit run',
        note: 'stored only in the config.tariff_versions.amended event payload; no view renders it',
    },
});
/**
 * A draft and one sample shipment, priced in the draft's own currency with no FX.
 *
 * The COD amount is in that currency too: a rate is platform configuration the draft does not
 * own, so the preview answers a question about the rate guide rather than about today's rate.
 */
export const tariffPreviewSchema = z
    .object({
    currency: z.enum(TARIFF_CURRENCIES),
    document: tariffDocumentSchema,
    cells: z.array(tariffCellInputSchema).min(1).max(2000),
    surchargeZones: z.array(tariffSurchargeZoneInputSchema).max(2000),
    sample: z
        .object({
        originClusterId: z.string().min(1),
        destinationClusterId: z.string().min(1),
        destinationZoneId: z.string().min(1),
        parcels: z.array(parcelInputSchema).min(1).max(20),
        codAmountMinor: minorUnits,
        declaredValueMinor: minorUnits.nullable(),
    })
        .strict(),
})
    .strict();
const PREVIEW_PRICED = 'priced through BigInt and never echoed as sent: the preview stores nothing and shows computed amounts';
const PREVIEW_MATCHED = 'matched against the draft lanes and Zones and never echoed: the preview stores nothing';
export const tariffPreviewRendered = renderedText(tariffPreviewSchema, {
    // The one string the preview echoes as sent.
    refs: ['document.rtoFeeMinor'],
    exempt: {
        'document.chargeableWeight': PREVIEW_PRICED,
        'document.minimumCharge': PREVIEW_PRICED,
        'document.codFee': PREVIEW_PRICED,
        'document.surcharges': 'codes limited by fee_code_shape (a letter first, word characters only); amounts and weights ' +
            'priced through BigInt and echoed as computed',
        cells: 'priced through BigInt and never echoed: the preview names the lane it priced draft_<n>',
        surchargeZones: PREVIEW_MATCHED,
        'sample.originClusterId': PREVIEW_MATCHED,
        'sample.destinationClusterId': PREVIEW_MATCHED,
        'sample.destinationZoneId': PREVIEW_MATCHED,
        'sample.codAmountMinor': PREVIEW_PRICED,
        'sample.declaredValueMinor': PREVIEW_PRICED,
    },
});
//# sourceMappingURL=tariff.js.map